import { FC, useState } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import NiceModal from '@ebay/nice-modal-react';
import CloseLogo from '../logos/CloseLogo';
import TextInput from '../form/TextInput';
import { FormProvider, useForm } from 'react-hook-form';
import Select from '../form/Select';
import ControlledNumericInput from '../form/ControlledNumericInput';
import { TrashIcon } from '@heroicons/react/24/solid';
import ApiService from '@/api';
import { METHODS } from '@/constants/api';

interface OptimaleAngle {
  optimal_angle: number;
  optimal_azimuth: number;
}

const PvDetailsModal: FC<{
  values: Record<string, string>;
  removePolygon: () => void;
}> = ({ values, removePolygon }) => {
  const { remove: removeModal, resolve, hide } = useModal();
  const form = useForm({
    defaultValues: {
      MODAL: values,
    },
  });
  const [optimalLoader, setOptimalLoader] = useState(false);
  const pvType = form.watch('MODAL.type_pv');
  const isExistingSystem = pvType === 'Bestandsanlage';

  const remove = () => {
    hide();
    removeModal();
  };

  const removePolygonAndModal = () => {
    removePolygon();
    remove();
  };

  const onConfirm = () => {
    form.handleSubmit(() => {
      resolve(form.getValues().MODAL);
      hide();
      removeModal();
    })();
  };

  const optimalCalculation = async () => {
    setOptimalLoader(true);
    const result = await ApiService.request<OptimaleAngle>({
      endpoint: `/dashboard/optimize_pv_orientation_yield/`,
      method: METHODS.GET,
    });
    const { optimal_angle, optimal_azimuth } = result;
    console.log('Optimal angle:', optimal_angle);
    console.log('Optimal azimuth:', optimal_azimuth);
    form.setValue('MODAL.angle_pv', String(optimal_angle));
    form.setValue('MODAL.orientation_pv_angle', String(optimal_azimuth));
    setOptimalLoader(false);
  };

  return (
    <div className="buildings">
      <div className="fixed inset-0	flex" style={{ zIndex: 99999 }}>
        <div className="absolute inset-0 bg-slate-900 opacity-50	-z-10"></div>

        <div className="max-h-[100vh] overflow-auto md:min-w-[594px] flex flex-col px-4 pt-4 pb-10 rounded-2xl bg-white m-auto">
          <button type="button" className="self-end text-right" onClick={remove}>
            <CloseLogo />
          </button>

          <div className="flex flex-col gap-4  text-center">
            <h2 className="not-italic font-bold text-3xl text-center text-[#333333]">PV Details</h2>
          </div>
          <form action="" className="pt-4 pb-0 px-4">
            <FormProvider {...form}>
              <TextInput
                className={'!text-black'}
                is_required
                model={'MODAL'}
                name={'name_pv'}
                label={'Name der Fläche (frei wählbar)'}
                page={'page'}
                type={'text'}
                category={'text'}
                description={'name_pv'}
              />
              <Select
                className={'!text-black'}
                is_required
                model={'MODAL'}
                name={'type_pv'}
                label={'Anlagentyp (Neu/Alt)'}
                page={'page'}
                type={'select'}
                options={['Neuanlage', 'Bestandsanlage']}
                category={'text'}
                description={'Wo soll die Anlage installiert werden?'}
              />
              <ControlledNumericInput
                className={'!text-black'}
                model={'MODAL'}
                name={'area_pv'}
                label={'Ausgewählte Fläche'}
                page={'page'}
                type={'float'}
                category={'text'}
                unit={'m2'}
                description={'Nutzbare Fläche für die PV-Anlage in m2.'}
              />
              {!isExistingSystem && (
                <ControlledNumericInput
                  className={'!text-black'}
                  model={'MODAL'}
                  name={'percentage_of_area_covered'}
                  label={'Belegbarer Anteil der Fläche'}
                  page={'page'}
                  type={'float'}
                  category={'text'}
                  unit={'%'}
                  defaultValue={100}
                  description={'Geben Sie hier den Anteil der Fläche an, der mit PV-Modulen belegt werden kann.'}
                />
              )}
              <div className="flex flex-col md:flex-row md:gap-4">
                <Select
                  model={'MODAL'}
                  category={'text'}
                  name={'orientation_pv'}
                  label={'Ausrichtung (optional)'}
                  page={'page'}
                  type={'select'}
                  options={['Nord', 'Nord-Ost', 'Ost', 'Süd-Ost', 'Süd', 'Süd-West', 'West', 'Nord-West']}
                  className="!text-black"
                  description={'Durch Angabe der Ausrichtung kann der PV Ertrag genauer abgeschätzt werden.'}
                  onChange={async selectedDirection => {
                    try {
                      const result = await ApiService.request<{ angle: number }>({
                        endpoint: `/dashboard/angle_from_direction/?azimuth=${selectedDirection}`,
                        method: METHODS.GET,
                      });
                      form.setValue('MODAL.orientation_pv_angle', String(result.angle));
                    } catch (error) {
                      console.error('Fehler beim Abrufen des Winkels:', error);
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      //@ts-ignore
                      if (error.response) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        //@ts-ignore
                        console.error('Server responded with:', error.response.data);
                      }
                    }
                  }}
                />
                <ControlledNumericInput
                  className={'!text-black'}
                  model={'MODAL'}
                  name={'orientation_pv_angle'}
                  label={'Ausrichtungswinkel (optional, Nord = 0°, Ost = 90°, usw.)'}
                  page={'page'}
                  type={'float'}
                  category={'text'}
                  unit={'°'}
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  defaultValue={null}
                  description={'Durch Angabe der Ausrichtung kann der PV Ertrag genauer abgeschätzt werden.'}
                />
              </div>
              <ControlledNumericInput
                className={'!text-black'}
                model={'MODAL'}
                name={'angle_pv'}
                options={['0', '10', '20', '30', '45']}
                label={'Neigung der PV-Module (optional, falls bekannt)'}
                min={0}
                max={90}
                max_err={'Bitte geben Sie eine gültige Zahl ein'}
                page={'page'}
                type={'select'}
                category={'text'}
                unit={'°'}
                description={'Durch Angabe des Neigungswinkel kann der PV Ertrag genauer abgeschätzt werden.'}
              />
              <div className="flex flex-col items-start mt-4">
                <p className="mb-2 text-base text-gray-600"></p>
                <div className="flex flex-row gap-4">
                  <button
                    type="button"
                    onClick={optimalCalculation}
                    className="px-4 py-2 bg-greendark text-white rounded disabled:opacity-50"
                    disabled={optimalLoader}
                  >
                    {optimalLoader ? (
                      <svg
                        className="animate-spin h-6 w-6 text-slate-500"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M12 2a10 10 0 0110 10h-2a8 8 0 00-8-8V2z"
                        ></path>
                      </svg>
                    ) : (
                      'Ertragsmaximierung'
                    )}
                  </button>
                  {/*<button
                        type="button"
                        onClick={async () => {
                            const result = await ApiService.request<OptimaleAngle>({
                                endpoint: `/api/dashboard/optimize_pv_orientation_loadprofile/`,
                                method: METHODS.GET,
                            });
                            const { optimal_angle, optimal_azimuth } = result;
                            console.log('Optimal angle:', optimal_angle);
                            console.log('Optimal azimuth:', optimal_azimuth);
                            form.setValue('MODAL.angle_pv', String(optimal_angle)); // Neigung ins Formular setzen
                            form.setValue('MODAL.orientation_pv', String(optimal_azimuth)); // Azimuth ins Formular setzen
                        }}
                        className="px-4 py-2 bg-greendark text-white rounded"
                    >
                        Anhand Ihres Lastgangs
                    </button>*/}
                </div>
              </div>

              {/* Existing System Fields */}
              {isExistingSystem && (
                <div className=" w-full">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800 border-b pb-2">Bestandsanlage</h3>
                  <div className="space-y-4">
                    <ControlledNumericInput
                      className={'!text-black'}
                      model={'MODAL'}
                      name={'installation_year_pv'}
                      label={'Installationsjahr (Optional)'}
                      page={'page'}
                      type="integer"
                      min={1990}
                      max={new Date().getFullYear()}
                      category={'text'}
                      description={'In welchem Jahr wurde die Anlage installiert?'}
                    />
                    <ControlledNumericInput
                      className={'!text-black'}
                      model={'MODAL'}
                      name={'nominal_power_pv'}
                      label={'Nennleistung (Optional)'}
                      page={'page'}
                      type="float"
                      unit="kWp"
                      category={'text'}
                      description={'Geben Sie die Nennleistung der bestehenden Anlage an'}
                    />
                    <ControlledNumericInput
                      className={'!text-black'}
                      model={'MODAL'}
                      name={'annual_yield_pv'}
                      label={'Jahresertrag des Vorjahres (Optional)'}
                      page={'page'}
                      type="float"
                      unit="kWh"
                      category={'text'}
                      description={'Durchschnittlicher Jahresertrag der Anlage'}
                    />
                    <ControlledNumericInput
                      className={'!text-black'}
                      model={'MODAL'}
                      name={'evq_pv'}
                      label={'Eigenverbrauchsquote'}
                      page={'page'}
                      type="float"
                      unit="%"
                      category={'text'}
                      description={'Geben Sie hier die Eigenverbrauchsquote der Anlage an'}
                    />
                  </div>
                </div>
              )}
            </FormProvider>
          </form>
          <div className="flex items-center gap-4 mt-8">
            <div>
              <button
                className="btn-modal border-[var(--grey-scale-300)] text-accent hover:bg-[var(--grey-scale-100)] transition"
                type="button"
                onClick={removePolygonAndModal}
              >
                <TrashIcon style={{ color: 'var(--pv-btn-color)' }} className="icon-color" />
              </button>
            </div>
            <div className="flex gap-2 md:ml-[280px] ml-[30px]">
              <button
                className="block w-full rounded border border-stroke bg-gray p-3 text-center font-medium !text-black transition hover:border-meta-1 hover:bg-meta-1 hover:text-black hover:opacity-50 dark:border-strokedark dark:bg-meta-4 dark:text-white dark:hover:border-meta-1 dark:hover:bg-meta-1"
                type="button"
                onClick={remove}
              >
                Abbrechen
              </button>
              <button
                style={{ backgroundColor: 'var(--pv-btn-color)' }}
                className="block min-w-[154px] rounded border border-none p-3 text-center font-medium text-black transition hover:bg-opacity-90"
                type="button"
                onClick={onConfirm}
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NiceModal.create(PvDetailsModal);
